import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  FolderKanban, 
  RefreshCw, 
  Wifi, 
  WifiOff, 
  DollarSign,
  Users,
  TrendingUp,
  Loader2
} from 'lucide-react';

const Projects = () => {
  const { projects, currentProject, switchProject } = useProject();
  const { api } = useAuth();
  const [platformStatus, setPlatformStatus] = useState(null);
  const [kazilinkData, setKazilinkData] = useState(null);
  const [sitedeskData, setSitedeskData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlatformData = async () => {
    setLoading(true);
    try {
      const [status, kazilinkCommission, sitedeskRevenue] = await Promise.all([
        api('/api/platforms/status'),
        api('/api/platforms/kazilink/commission-summary'),
        api('/api/platforms/sitedesk/revenue-summary')
      ]);
      setPlatformStatus(status);
      setKazilinkData(kazilinkCommission);
      setSitedeskData(sitedeskRevenue);
    } catch (err) {
      console.error('Failed to fetch platform data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatformData();
  }, []);

  const getConnectorBadge = (platform) => {
    const status = platformStatus?.[platform];
    if (!status) return null;
    
    const isConnected = status.status === 'connected' || status.mode === 'mock';
    return (
      <Badge 
        variant={isConnected ? 'default' : 'destructive'} 
        className="text-xs gap-1"
      >
        {status.mode === 'mock' ? (
          <>MOCK</>
        ) : isConnected ? (
          <><Wifi className="w-3 h-3" /> Live</>
        ) : (
          <><WifiOff className="w-3 h-3" /> Offline</>
        )}
      </Badge>
    );
  };

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">Manage your workspace projects & platform connections</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchPlatformData}
          disabled={loading}
          data-testid="refresh-platforms-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* KaziLink Card */}
        {projects.filter(p => p.type === 'kazilink').map((project) => {
          const isActive = currentProject?.project_id === project.project_id;
          return (
            <Card 
              key={project.project_id}
              className={`bg-card border-border card-glow cursor-pointer transition-all ${isActive ? 'ring-2 ring-primary' : ''}`}
              onClick={() => switchProject(project.project_id)}
              data-testid="project-card-kazilink"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <span className="text-2xl">🇰🇪</span>
                    {project.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    {getConnectorBadge('kazilink')}
                    {isActive && <Badge variant="secondary">Active</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Domain:</span> {project.domain}</div>
                  <div><span className="text-muted-foreground">Region:</span> {project.region}</div>
                  <div><span className="text-muted-foreground">Currency:</span> {project.currency}</div>
                  <div><span className="text-muted-foreground">Model:</span> 5% Commission</div>
                </div>
                
                {/* KaziLink Finance Summary */}
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : kazilinkData && (
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                      Commission Ledger
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">Total Earned</div>
                        <div className="font-medium">KES {kazilinkData.total_commission_earned?.toLocaleString() || 0}</div>
                      </div>
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">Pending</div>
                        <div className="font-medium">KES {kazilinkData.commission_pending?.toLocaleString() || 0}</div>
                      </div>
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">Transactions</div>
                        <div className="font-medium">{kazilinkData.transaction_count || 0}</div>
                      </div>
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">Rate</div>
                        <div className="font-medium">{kazilinkData.commission_rate}</div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* SiteDesk Card */}
        {projects.filter(p => p.type === 'sitedesk').map((project) => {
          const isActive = currentProject?.project_id === project.project_id;
          return (
            <Card 
              key={project.project_id}
              className={`bg-card border-border card-glow cursor-pointer transition-all ${isActive ? 'ring-2 ring-primary' : ''}`}
              onClick={() => switchProject(project.project_id)}
              data-testid="project-card-sitedesk"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <span className="text-2xl">🇦🇺</span>
                    {project.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    {getConnectorBadge('sitedesk')}
                    {isActive && <Badge variant="secondary">Active</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Domain:</span> {project.domain}</div>
                  <div><span className="text-muted-foreground">Region:</span> {project.region}</div>
                  <div><span className="text-muted-foreground">Currency:</span> {project.currency}</div>
                  <div><span className="text-muted-foreground">Model:</span> Stripe Subscriptions</div>
                </div>
                
                {/* SiteDesk Revenue Summary */}
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : sitedeskData && (
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <TrendingUp className="w-4 h-4 text-blue-400" />
                      Subscription Metrics
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">MRR</div>
                        <div className="font-medium">AUD ${sitedeskData.mrr?.toLocaleString() || 0}</div>
                      </div>
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">ARR</div>
                        <div className="font-medium">AUD ${sitedeskData.arr?.toLocaleString() || 0}</div>
                      </div>
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">Active Subs</div>
                        <div className="font-medium">{sitedeskData.active_subscriptions || 0}</div>
                      </div>
                      <div className="bg-background/50 rounded p-2">
                        <div className="text-muted-foreground text-xs">Churn Rate</div>
                        <div className="font-medium">{sitedeskData.churn_rate?.toFixed(1) || 0}%</div>
                      </div>
                    </div>
                    
                    {/* Plan Breakdown */}
                    {sitedeskData.plan_breakdown?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sitedeskData.plan_breakdown.map((plan) => (
                          <Badge key={plan.plan} variant="outline" className="text-xs">
                            {plan.plan}: {plan.count} (${plan.mrr})
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Projects;
